import React, { useEffect, useRef, useState } from 'react';
import { Search as SearchIcon, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { Input } from './ui/input';

/**
 * Smart autocomplete search. Type ≥ 2 chars → debounced /api/recipes/suggest
 * hit returns thumbnails. Click a suggestion to jump straight to the recipe.
 * Enter submits a full text search (delegated to `onSubmit` prop).
 */
export default function SearchSuggest({
  value, onChange,
  onSubmit,
  placeholder,
  inputTestId = 'name-search-input',
}) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const reqId = useRef(0);

  // Debounced fetch
  useEffect(() => {
    const q = (value || '').trim();
    if (q.length < 2) {
      setItems([]); setLoading(false); setOpen(false); return;
    }
    setLoading(true);
    const tid = setTimeout(async () => {
      const myReq = ++reqId.current;
      try {
        const { data } = await api.get('/recipes/suggest', { params: { q, limit: 8 } });
        // discard stale responses
        if (myReq !== reqId.current) return;
        setItems(data);
        setOpen(true);
        setActive(-1);
      } catch (err) {
        console.warn('[SearchSuggest] fetch failed', err);
        setItems([]);
        setOpen(false);
      } finally {
        if (myReq === reqId.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(tid);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pickItem = (item) => {
    setOpen(false);
    navigate(`/recipe/${item.recipe_id}`);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && items.length > 0) {
      setOpen(true);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && items[active]) {
        e.preventDefault();
        pickItem(items[active]);
      } else if (onSubmit) {
        e.preventDefault();
        onSubmit();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1" data-testid="search-suggest">
      <div className="relative">
        <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C4D44] dark:text-neutral-400 pointer-events-none" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => items.length > 0 && setOpen(true)}
          placeholder={placeholder || t('namePlaceholder')}
          className="ps-9 pe-9"
          data-testid={inputTestId}
          aria-autocomplete="list"
          aria-expanded={open}
          role="combobox"
        />
        {loading && (
          <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#C05A42]" />
        )}
        {!loading && value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-[#5C4D44] hover:text-[#C05A42]"
            aria-label={t('exit')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && items.length > 0 && (
        <ul
          data-testid="suggest-dropdown"
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-[70vh] overflow-auto rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-neutral-900 shadow-2xl"
        >
          {items.map((it, i) => {
            const title = lang === 'ar' ? it.title_ar : it.title_en;
            return (
              <li
                key={it.recipe_id}
                role="option"
                aria-selected={active === i}
                data-testid={`suggest-${it.recipe_id}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); pickItem(it); }}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition ${
                  active === i ? 'bg-[#EFEBE1] dark:bg-neutral-800' : 'hover:bg-[#F9F6F0] dark:hover:bg-neutral-800/60'
                }`}
              >
                <img
                  src={it.image}
                  alt=""
                  loading="lazy"
                  className="h-12 w-12 rounded-xl object-cover shrink-0 ring-1 ring-black/5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2C2420] dark:text-neutral-100 truncate">
                    {title}
                  </div>
                  <div className="text-xs text-[#5C4D44] dark:text-neutral-400 truncate">
                    {it.category} {it.time ? `· ${it.time} ${t('minutes') || 'min'}` : ''}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
